import { zipCodeToState } from '@shared';
import {
  AdvisorInfoWithWritingCode,
  checkForSpecificConditions,
  checkIfEnrolledInMedicaid,
  checkMedicalApplianceUsage,
  convertHeightToFeetAndInches,
  determineCoupleStatus,
  formatDateForForm,
  GetAdvisorAddress,
  getClientWeightChange,
  getSubstanceTypes,
  mapGender,
  mapMaritalStatus,
  returnSafeString,
  tobaccoUseOptions,
} from './carrier-utils';

export const CARE_SCOUT_PREFILL_SECTIONS_PRIMARY = [
  'applicant-profile',
  'insurability-profile',
  'medical-questions',
  'social-history',
  'family-history',
  'details',
  'other-coverage',
  'ltc-insurance-personal-worksheet',
];

export const getClientUpdatesForCarescout = (clientInfo: Client): PreFillUpdate[] => {
  const clientUpdates = [
    {
      questionRef: 'firstName',
      answer: clientInfo.clientFirstName,
    },
    {
      questionRef: 'lastName',
      answer: clientInfo.clientLastName,
    },
    {
      questionRef: 'applicantPrintName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'applicantNameEft',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'applicantName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'emailAddress',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
  ].filter(update => update.answer);

  const clientPartnerUpdates = clientInfo.mutableClientPartner
    ? [
        {
          questionRef: 'coupleStatus',
          answer: 'yes',
        },
        {
          questionRef: 'spouseName',
          answer: clientInfo.mutableClientPartner.clientFullName,
          sourceQuestionRefs: ['partnerFirstName', 'partnerLastName'],
        },
        {
          questionRef: 'spouseDob',
          answer: formatDateForForm(clientInfo.mutableClientPartner.intakeSurvey?.clientBirthDate),
        },
      ].filter(update => update.answer)
    : [];

  const directMedicalMappings = getDirectIntakeMappings(clientInfo);

  return [...clientUpdates, ...clientPartnerUpdates, ...directMedicalMappings];
};

const getDirectIntakeMappings = (clientInfo: Client): PreFillUpdate[] => {
  if (clientInfo?.intakeSurvey) {
    const { intakeSurvey } = clientInfo;
    const heightData = convertHeightToFeetAndInches(intakeSurvey.clientHeightInches);
    const weightChangeData = getClientWeightChange(
      intakeSurvey.clientWeightGainedLost,
      intakeSurvey.clientWeightGainedLastTwelveMonths,
      intakeSurvey.clientWeightLostLastTwelveMonths,
    );

    const clientMappings = [
      {
        questionRef: 'maritalStatus',
        answer: mapMaritalStatus(intakeSurvey.clientMaritalStatus, 'careScout'),
        sourceQuestionRefs: ['clientMaritalStatus'],
      },
      {
        questionRef: 'coupleStatus',
        answer: determineCoupleStatus(intakeSurvey.clientMaritalStatus),
        sourceQuestionRefs: ['clientMaritalStatus'],
      },
      {
        questionRef: 'gender',
        answer: mapGender(intakeSurvey.clientGenderAtBirth),
        sourceQuestionRefs: ['clientGenderAtBirth'],
      },
      {
        questionRef: 'dateOfBirth',
        answer: formatDateForForm(intakeSurvey?.clientBirthDate),
        sourceQuestionRefs: ['clientBirthDate'],
      },
      {
        questionRef: 'applicantDob',
        answer: formatDateForForm(clientInfo.intakeSurvey?.clientBirthDate),
        sourceQuestionRefs: ['clientBirthDate'],
      },
      {
        questionRef: 'residenceZipCode',
        answer: intakeSurvey?.clientZipCode,
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'residenceState',
        answer: zipCodeToState(intakeSurvey?.clientZipCode).stateAbbreviation || '',
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'socialSecurityDisability',
        answer: intakeSurvey?.clientDisabilityWorkersComp?.toLowerCase() === 'yes' ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDisabilityWorkersComp'],
      },
      {
        questionRef: 'assistiveDevices',
        answer: checkMedicalApplianceUsage(intakeSurvey.clientAssistiveDevicesList) ? 'yes' : 'no',
        sourceQuestionRefs: ['clientAssistiveDevicesList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'adlAssistance',
        answer: intakeSurvey.clientAdlReceivedAssistanceCount > 0 ? 'yes' : 'no',
        sourceQuestionRefs: ['clientAdlReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'ltcFacility',
        answer: intakeSurvey.clientHasStartedLtc ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDifficultyWithTasks', 'clientAdlReceivedAssistanceList'],
      },
      {
        questionRef: 'heightFeet',
        answer: heightData.feet > 0 ? heightData.feet.toString() : '',
        sourceQuestionRefs: ['clientHeightInches'],
      },
      {
        questionRef: 'heightInches',
        answer: heightData.feet > 0 ? heightData.inches.toString() : '',
        sourceQuestionRefs: ['clientHeightInches'],
      },
      {
        questionRef: 'weight',
        answer:
          (intakeSurvey.clientWeightPounds || 0) > 0
            ? intakeSurvey.clientWeightPounds.toString()
            : '',
        sourceQuestionRefs: ['clientWeightPounds'],
      },
      {
        questionRef: 'weightChange',
        answer: weightChangeData.weightGained === undefined ? 'no' : 'yes',
        sourceQuestionRefs: ['clientWeightGainedLost'],
      },
      {
        questionRef: 'weightChangeType',
        answer:
          weightChangeData.weightGained === undefined
            ? ''
            : weightChangeData.weightGained
              ? 'gained'
              : 'lost',
        sourceQuestionRefs: ['clientWeightGainedLost'],
      },
      {
        questionRef: 'weightChangePounds',
        answer: weightChangeData.weightChangeAmount.toString(),
        sourceQuestionRefs: [
          'clientWeightGainedLastTwelveMonths',
          'clientWeightLostLastTwelveMonths',
        ],
      },
      {
        questionRef: 'anginaChestPains',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'angina',
          'chest pain',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'heartAttack',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'heart attack',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'heartDisease',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'heart disease',
          'heart disorder',
          'Coronary Artery Disease',
          'CAD',
          'Congestive Heart Failure',
          'Atrial fibrillation or other arrhythmia',
          'Cardiomyopathy or congestive heart failure (CHF)',
          'Peripheral vascular disease / aneurysm',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'heartMurmur',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'heart murmur',
          'Valvular Heart Disease',
          'Heart valve disease / murmur',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'highBloodPressure',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'high blood pressure',
          'hypertension',
        ]),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'irregularHeartbeat',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'irregular heartbeat',
          'arrhythmia',
          'Atrial fibrillation or other arrhythmia',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'peripheralVascular',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'peripheral vascular disease',
          'peripheral artery disease',
          'PAD',
          'Peripheral vascular disease / aneurysm',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'colitisCrohns',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'colitis',
          "Crohn's disease",
          "Ulcerative Colitis / Crohn's Disease (IBD)",
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'esophagusDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'esophagus disorder',
          'esophageal reflux',
          'GERD',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'hepatitis',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'hepatitis',
          'Hepatitis A / B / C',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'intestinalDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'intestinal disorder',
          'Irritable bowel syndrome (IBS)',
          'Irritable bowl syndrome (IBS)',
          'Inflammatory Bowel Disease (IBD)',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'liverDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'liver disorder',
          'Hepatitis A / B / C',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'pancreatitis',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, ['pancreatitis']),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'stomachDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'stomach disorder',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'diabetes',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['diabetes']),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'cancer',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'cancer',
          'Cancer, tumor, polyp, cyst',
        ]),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'leukemiaLymphoma',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
          'Leukemia',
          'Lymphoma',
        ]),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'tumorMassLump',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
          'Tumor',
          'Mass',
          'Lump',
          'Skin Lesion',
          'Skin Disorder',
          'Skin Cancer',
          'Skin',
        ]),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'asthma',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, ['Asthma']),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'chronicLungDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'COPD / emphysema',
          'Chronic bronchitis',
          'Chronic lung disorder',
          'Chronic obstructive pulmonary disease (COPD)',
          'Emphysema',
          'Bronchitis',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'sleepApnea',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'Sleep apnea',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'macularDegeneration',
        answer: checkForSpecificConditions(intakeSurvey.clientEENTDiagnosis, [
          'Macular degeneration',
        ]),
        sourceQuestionRefs: ['clientEENTDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'bipolarDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'Bipolar Disorder (Manic Depression)',
          'Bipolar Disorder',
          'Bipolar',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'depression',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, ['Depression']),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'otherMentalDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'Anxiety',
          'Post-traumatic stress disorder (PTSD)',
          'Schizophrenia',
          'Borderline personality disorder (BPD)',
          'Obsessive-compulsive disorder (OCD)',
          'Attention deficit hyperactivity disorder (ADHD)',
          'Mental illness',
          'Mental disorder',
          'OCD',
          'ADHD',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'bladderDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'Bladder issues',
          'Bladder disorder',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'kidneyDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'Chronic kidney disease',
          'Kidney disorder',
          'Kidney failure',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'prostateDisorder',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'Prostate issues',
          'Prostate disorder',
          'Prostate enlargement',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'arthritis',
        answer:
          intakeSurvey.clientInjuryArthritis === 'Yes' ||
          checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
            'Rheumatoid arthritis',
          ]) === 'yes'
            ? 'yes'
            : intakeSurvey.clientInjuryArthritis === 'No'
              ? 'no'
              : '',
        sourceQuestionRefs: [
          'clientInjuryArthritis',
          'clientImmuneSystemDiagnosis',
          'clientDiagnosesList',
          'clientInjuriesList',
        ],
      },
      {
        questionRef: 'memoryLoss',
        answer: checkForSpecificConditions(intakeSurvey.clientCognitiveMemoryDiagnosis, [
          'Memory loss',
          'Dementia',
          "Alzheimer's",
        ]),
        sourceQuestionRefs: ['clientCognitiveMemoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'brainNervousSystem',
        answer: intakeSurvey.clientBrainInjuryDiagnosis?.length > 0 ? 'yes' : 'no',
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'tremors',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Tremors',
          'Parkinson',
          'Parkinsonism',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'seizuresConvulsions',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Seizures',
          'Convulsions',
          'Epilepsy / seizures',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'strokeTia',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Single stroke',
          'TIA ("mini stroke")',
          'Multiple strokes',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'hivAids',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
          'HIV positive',
          'AIDS',
        ]),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'lupusScleroderma',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
          'Lupus',
          'Scleroderma',
        ]),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'additionalPrescriptionMedications',
        answer: intakeSurvey.clientMedicationIntake?.trim().length > 0 ? 'yes' : 'no',
        sourceQuestionRefs: ['clientMedicationIntake'],
      },
      {
        questionRef: 'medicationDetails',
        answer: intakeSurvey.clientMedicationIntake?.trim() || '',
        sourceQuestionRefs: ['clientMedicationIntake'],
      },
      {
        questionRef: 'hospitalPatient',
        answer:
          checkForSpecificConditions(intakeSurvey.clientHospitalizedERLastTwelveMonths, [
            'Hospitalized',
            'Visited the ER',
          ]) === 'yes'
            ? 'yes'
            : intakeSurvey.clientPriorTwoYearHospitalizationCount > 0
              ? 'yes'
              : 'no',
        sourceQuestionRefs: [
          'clientHospitalizedERLastTwelveMonths',
          'clientPriorTwoYearHospitalizationCount',
        ],
      },
      {
        questionRef: 'assistanceRequired',
        answer: intakeSurvey.clientActivitiesReceivedAssistanceList?.length > 0 ? 'yes' : 'no',
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'receivedHomeCare',
        answer: intakeSurvey.clientHasStartedLtc ? 'yes' : 'no',
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList'],
      },
      {
        questionRef: 'disabilityBenefits',
        answer:
          intakeSurvey?.clientDisabilityWorkersComp?.toLowerCase() === 'yes'
            ? 'other-disability'
            : '',
        sourceQuestionRefs: ['clientDisabilityWorkersComp'],
      },
      {
        questionRef: 'tobaccoUse',
        answer:
          checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, tobaccoUseOptions) === 'yes'
            ? 'yes'
            : intakeSurvey.clientSmokerEver?.toLowerCase() === 'yes'
              ? 'yes'
              : 'no',
        sourceQuestionRefs: ['clientSubstancesUsed', 'clientSmokerEver'],
      },
      {
        questionRef: 'tobaccoType',
        answer: getSubstanceTypes(intakeSurvey.clientSubstancesUsed, tobaccoUseOptions),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'drinkAlcohol',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Alcohol']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'drugUse',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
          'Marijuana',
          'Mushrooms',
          'Hard drugs',
        ]),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'drugSubstanceName',
        answer: getSubstanceTypes(intakeSurvey.clientSubstancesUsed, [
          'Marijuana',
          'Mushrooms',
          'Hard drugs',
        ]),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'fatherLiving',
        answer: intakeSurvey.clientCanAnswerForFather
          ? intakeSurvey.clientFatherIsAlive === "I don't know / prefer not to answer"
            ? 'unknown'
            : intakeSurvey.clientFatherIsAlive?.toLowerCase()
          : '',
        sourceQuestionRefs: ['clientCanAnswerForFather', 'clientFatherIsAlive'],
      },
      {
        questionRef: 'fatherAge',
        answer: intakeSurvey.clientCanAnswerForFather && intakeSurvey.clientFatherAgeAtSurveyDate,
        sourceQuestionRefs: ['clientCanAnswerForFather', 'clientFatherAgeAtSurveyDate'],
      },
      {
        questionRef: 'fatherAgeAtDeath',
        answer:
          intakeSurvey.clientCanAnswerForFather && intakeSurvey.clientFatherAgeOfDeathAtSurveyDate,
        sourceQuestionRefs: ['clientCanAnswerForFather', 'clientFatherAgeOfDeathAtSurveyDate'],
      },
      {
        questionRef: 'fatherAlzheimers',
        answer:
          intakeSurvey.clientCanAnswerForFather &&
          intakeSurvey.clientFatherMemoryProblemsEver === 'Yes' &&
          checkForSpecificConditions(intakeSurvey.clientFatherMemoryDiagnosesList, [
            "Alzheimer's",
            'Dementia',
          ]),
        sourceQuestionRefs: [
          'clientCanAnswerForFather',
          'clientFatherMemoryProblemsEver',
          'clientFatherMemoryDiagnosesList',
        ],
      },
      {
        questionRef: 'motherLiving',
        answer: intakeSurvey.clientCanAnswerForMother
          ? intakeSurvey.clientMotherIsAlive === "I don't know / prefer not to answer"
            ? 'unknown'
            : intakeSurvey.clientMotherIsAlive?.toLowerCase()
          : '',
        sourceQuestionRefs: ['clientCanAnswerForMother', 'clientMotherIsAlive'],
      },
      {
        questionRef: 'motherAge',
        answer: intakeSurvey.clientCanAnswerForMother && intakeSurvey.clientMotherAgeAtSurveyDate,
        sourceQuestionRefs: ['clientCanAnswerForMother', 'clientMotherAgeAtSurveyDate'],
      },
      {
        questionRef: 'motherAgeAtDeath',
        answer:
          intakeSurvey.clientCanAnswerForMother && intakeSurvey.clientMotherAgeOfDeathAtSurveyDate,
        sourceQuestionRefs: ['clientCanAnswerForMother', 'clientMotherAgeOfDeathAtSurveyDate'],
      },
      {
        questionRef: 'motherAlzheimers',
        answer:
          intakeSurvey.clientCanAnswerForMother &&
          intakeSurvey.clientMotherMemoryProblemsEver === 'Yes' &&
          checkForSpecificConditions(intakeSurvey.clientMotherMemoryDiagnosesList, [
            "Alzheimer's",
            'Dementia',
          ]),
        sourceQuestionRefs: [
          'clientCanAnswerForMother',
          'clientMotherMemoryProblemsEver',
          'clientMotherMemoryDiagnosesList',
        ],
      },
      {
        questionRef: 'currentlyEnrolledMedicaid',
        answer: checkIfEnrolledInMedicaid(intakeSurvey.clientHealthPlanList),
        sourceQuestionRefs: ['clientHealthPlanList'],
      },
      {
        questionRef: 'personalWorksheetHouseholdIncome',
        answer:
          typeof intakeSurvey.householdPriorYearIncome === 'number'
            ? intakeSurvey.householdPriorYearIncome < 30000
              ? 'under-30000'
              : intakeSurvey.householdPriorYearIncome <= 50000
                ? '30000-50000'
                : 'over-50000'
            : '',
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'personalWorksheetAssetsWorth',
        answer:
          typeof intakeSurvey.householdTotalAssetsValue === 'number'
            ? intakeSurvey.householdTotalAssetsValue < 30000
              ? 'under-30000'
              : intakeSurvey.householdTotalAssetsValue <= 50000
                ? '30000-50000'
                : 'over-50000'
            : '',
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
    ].filter(
      update => update.answer && (Array.isArray(update.answer) ? update.answer.length > 0 : true),
    );
    return clientMappings;
  }
  return [];
};

export const getPolicyUpdatesForCareScout = (
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  // Prefer updatedQuote if available (has illustration data), otherwise use sourceQuote
  let initialQuote = policyFundingSource.updatedQuote || policyFundingSource.sourceQuote;
  if (typeof initialQuote === 'string') {
    try {
      initialQuote = JSON.parse(initialQuote);
    } catch (e) {
      console.error('Failed to parse quote:', e);
      initialQuote = null;
    }
  }

  // Type guard to check if quote is a Traditional LTC (not PolicyQuote)
  const isPolicyQuote = (quote: any): quote is TraditionalLTCQuote => {
    return quote && typeof quote === 'object' && 'dailyBenefitAmount' in quote;
  };

  const sourceQuote = isPolicyQuote(initialQuote) ? initialQuote : null;

  return [
    {
      questionRef: 'coverageMaximum',
      answer: returnSafeString(policyFundingSource.policyMaximumBenefitAmount),
    },
    {
      questionRef: 'coverageMaximumSpecial',
      answer: returnSafeString(policyFundingSource.policyMaximumBenefitAmount),
    },
    {
      questionRef: 'dailyBenefitAmount',
      answer: returnSafeString(policyFundingSource.dailyHomeBenefitAmount),
    },
    {
      questionRef: 'dailyBenefitAmount50k',
      answer: returnSafeString(policyFundingSource.dailyHomeBenefitAmount),
    },
    {
      questionRef: 'dailyBenefitAmount100k',
      answer: returnSafeString(policyFundingSource.dailyHomeBenefitAmount),
    },
    {
      questionRef: 'dailyBenefitAmount150k',
      answer: returnSafeString(policyFundingSource.dailyHomeBenefitAmount),
    },
    {
      questionRef: 'dailyBenefitAmount200k',
      answer: returnSafeString(policyFundingSource.dailyHomeBenefitAmount),
    },
    {
      questionRef: 'dailyBenefitAmount250k',
      answer: returnSafeString(policyFundingSource.dailyHomeBenefitAmount),
    },
    {
      questionRef: 'deductiblePeriod',
      answer:
        policyFundingSource.homeCareWaitingPeriodDays === 90
          ? '90-days'
          : policyFundingSource.homeCareWaitingPeriodDays === 180
            ? '180-days'
            : '',
    },
    {
      questionRef: 'nonforfeitureBenefit',
      answer: sourceQuote?.nfoRider ? 'yes' : 'no',
    },
    {
      questionRef: 'inflationProtection',
      answer:
        typeof policyFundingSource.policyInflationProtection === 'number'
          ? policyFundingSource.policyInflationProtection === 0.03
            ? '3-compound'
            : policyFundingSource.policyInflationProtection === 0.05
              ? '5-compound'
              : policyFundingSource.policyInflationProtection === 0.01
                ? '1-compound'
                : 'no-increases'
          : 'no-increases',
    },
    {
      questionRef: 'premiumPaymentMode',
      answer:
        sourceQuote?.premiumFrequency === undefined
          ? ''
          : sourceQuote?.premiumFrequency === 'Annually'
            ? 'annual'
            : sourceQuote?.premiumFrequency === 'Monthly'
              ? 'monthly'
              : '',
    },
    {
      questionRef: 'personalWorksheetPremiumMonthly',
      answer:
        sourceQuote?.premiumFrequency === 'Monthly'
          ? returnSafeString(sourceQuote?.primaryModalPremium)
          : '',
    },
    {
      questionRef: 'personalWorksheetPremiumTotal',
      answer:
        sourceQuote?.premiumFrequency === 'Annually'
          ? returnSafeString(sourceQuote?.primaryModalPremium)
          : '',
    },
    {
      questionRef: 'personalWorksheetBuyInflationProtection',
      answer:
        typeof policyFundingSource.policyInflationProtection === 'number'
          ? policyFundingSource.policyInflationProtection > 0
            ? 'yes'
            : 'no'
          : 'no',
    },
    {
      questionRef: 'personalWorksheetDeductiblePeriod',
      answer:
        policyFundingSource.homeCareWaitingPeriodDays === 90
          ? '90-days'
          : policyFundingSource.homeCareWaitingPeriodDays === 180
            ? '180-days'
            : '',
    },
    {
      questionRef: 'premiumAmount',
      answer: returnSafeString(sourceQuote?.primaryTotalYearlyPremium ?? null),
    },
  ].filter(update => update.answer);
};

export const getAdvisorUpdatesForCareScout = (
  advisorInfo: AdvisorInfoWithWritingCode,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'agentName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'producerInfoPhone',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'producerInfoName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'producerInfoEmail',
      answer: advisorInfo.advisorEmail || '',
    },
    {
      questionRef: 'producerInfoBrokerageAgency',
      answer: advisorInfo.organizationName ?? '',
    },
    {
      questionRef: 'agentProducerCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'producerInfoStreetAddress',
      answer: GetAdvisorAddress(advisorInfo.advisorBusinessAddress),
    },
    {
      questionRef: 'producerInfoCity',
      answer: advisorInfo.advisorBusinessAddress?.city || '',
    },
    {
      questionRef: 'producerInfoState',
      answer: advisorInfo.advisorBusinessAddress?.state || '',
    },
    {
      questionRef: 'producerInfoZipcode',
      answer: advisorInfo.advisorBusinessAddress?.zipCode || '',
    },
    {
      questionRef: 'ackAgentName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
  ].filter(update => update.answer);
};
